import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Flame, Heart, Wand2, ArrowRight, Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GRADIENT_MAP = {
  'cyan-magenta': 'linear-gradient(135deg, #00F0FF 0%, #FF007F 100%)',
  'silver-slate': 'linear-gradient(135deg, #e2e8f0 0%, #64748b 100%)',
  'emerald-lime': 'linear-gradient(135deg, #10b981 0%, #a3e635 100%)',
  'amber-crimson': 'linear-gradient(135deg, #f59e0b 0%, #dc2626 100%)',
  'sky-lavender': 'linear-gradient(135deg, #38bdf8 0%, #c084fc 100%)',
  'violet-gold': 'linear-gradient(135deg, #8b5cf6 0%, #fbbf24 100%)',
};
const gradientFor = (k) => GRADIENT_MAP[k] || GRADIENT_MAP['cyan-magenta'];

export default function TrendingSouls() {
  const [souls, setSouls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/souls?sort=top&limit=6`);
        if (r.ok) setSouls(await r.json());
      } catch { /* noop */ }
      setLoading(false);
    })();
  }, []);

  if (loading || souls.length === 0) return null;

  return (
    <section
      id="trending-souls"
      data-testid="landing-trending-souls"
      className="relative py-24 md:py-32 bg-[#020202] text-white overflow-hidden"
    >
      {/* Ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/4 w-[520px] h-[520px] rounded-full blur-3xl opacity-20"
        style={{ background: 'radial-gradient(circle, #00F0FF 0%, transparent 70%)' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 right-1/4 w-[520px] h-[520px] rounded-full blur-3xl opacity-20"
        style={{ background: 'radial-gradient(circle, #FF007F 0%, transparent 70%)' }}
      />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] backdrop-blur-xl px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.3em] text-white/70">
              <Flame size={12} className="text-amber-300" />
              Trending SOULs
            </div>
            <h2
              className="mt-5 font-display font-black tracking-[-0.03em] text-4xl md:text-5xl"
              style={{
                background: 'linear-gradient(180deg, #ffffff 0%, #e2e8f0 55%, #94a3b8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              The personalities everyone's using this week.
            </h2>
            <p className="mt-3 text-white/60 text-[15px] max-w-2xl leading-relaxed">
              Tap any card to load it into the builder. Publish your own — every share brings new fans.
            </p>
          </div>
          <Link
            to="/souls"
            data-testid="landing-trending-view-all"
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.1] text-white text-[13.5px] font-medium px-5 py-2.5 transition"
          >
            View all souls <ArrowRight size={13} />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {souls.slice(0, 6).map((s, i) => (
            <motion.div
              key={s.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
            >
              <Link
                to={`/souls/${s.slug}`}
                data-testid={`landing-trending-card-${s.slug}`}
                className="group block rounded-2xl border border-white/10 bg-black/40 backdrop-blur-xl overflow-hidden hover:border-white/25 transition"
              >
                {/* Gradient header */}
                <div className="h-20 relative" style={{ background: gradientFor(s.gradient) }}>
                  <div className="absolute inset-0 opacity-30 mix-blend-overlay" style={{
                    background: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.4), transparent 45%)',
                  }} />
                  <div className="absolute bottom-2 left-3 flex items-center gap-2">
                    <div className="h-10 w-10 rounded-lg bg-black/40 backdrop-blur-md border border-white/20 flex items-center justify-center text-xl">
                      {s.emoji || (s.name || 'S').slice(0, 1)}
                    </div>
                    <div>
                      <div className="text-[13px] font-black text-white leading-tight drop-shadow">
                        {s.name}
                      </div>
                      <div className="text-[9.5px] text-white/80 font-mono uppercase tracking-widest">
                        by {s.author_name}
                      </div>
                    </div>
                  </div>
                  {s.featured && (
                    <div className="absolute top-2 right-2 rounded-full bg-black/50 backdrop-blur-md border border-white/20 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-white flex items-center gap-1">
                      <Star size={9} className="text-amber-300" fill="currentColor" /> Top
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <p className="text-[12px] text-white/70 leading-relaxed line-clamp-2 min-h-[30px]">
                    {s.tagline}
                  </p>
                  <div className="mt-2 flex items-center justify-between text-[11px] text-white/50">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center gap-1"><Heart size={10} /> {s.likes}</span>
                      <span className="inline-flex items-center gap-1"><Wand2 size={10} /> {s.uses}</span>
                    </div>
                    <span className="text-[10px] group-hover:text-white transition inline-flex items-center gap-0.5">
                      Open <ArrowRight size={10} />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* CTA banner */}
        <div className="mt-10 rounded-3xl border border-white/10 overflow-hidden relative">
          <div
            className="absolute inset-0 pointer-events-none opacity-30"
            style={{ background: 'radial-gradient(circle at 20% 20%, #00F0FF33, transparent 55%), radial-gradient(circle at 80% 80%, #FF007F33, transparent 55%)' }}
          />
          <div className="relative p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5">
            <Sparkles size={40} className="text-[#00F0FF] shrink-0" />
            <div className="flex-1">
              <div className="text-2xl md:text-3xl font-display font-black tracking-tight text-white">
                Publish your SOUL. Own a shareable URL.
              </div>
              <div className="mt-2 text-white/65 text-[14px] max-w-2xl">
                Every persona you publish gets a beautiful auto-generated share card. Post it once — new builders keep discovering it.
              </div>
            </div>
            <Link
              to="/souls"
              data-testid="landing-trending-cta"
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full text-black font-semibold text-[14px] px-6 py-3 hover:scale-[1.02] transition"
              style={{ background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)' }}
            >
              Publish your SOUL <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
